const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getSafeLink = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return null;

  try {
    const parsedUrl = new URL(rawValue, window.location.origin);
    const isHttp = parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
    if (!isHttp) return null;

    return {
      href: parsedUrl.toString(),
      isExternal: parsedUrl.origin !== window.location.origin,
    };
  } catch (error) {
    console.warn("Banner link invalido:", error);
    return null;
  }
};

class BannerCarousel {
  constructor(container, banners = []) {
    this.container = container;
    this.banners = banners.filter((banner) => banner.active);
    this.currentIndex = 0;
    this.intervalId = null;
    this.autoPlayDelay = 6000;
    this.isInteractionPaused = false;
    this.userPaused = Boolean(
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
    this.root = null;
    this.liveRegion = null;
    this.prevButton = null;
    this.nextButton = null;
    this.toggleButton = null;
    this.slides = [];
    this.indicators = [];

    this.init();
  }

  init() {
    this.stopAutoPlay();

    if (this.banners.length === 0) {
      this.container.hidden = true;
      this.container.innerHTML = "";
      return;
    }

    this.container.hidden = false;
    this.render();
    this.cacheElements();
    this.bindEvents();
    this.goTo(0);
    this.startAutoPlay();
  }

  render() {
    const totalSlides = this.banners.length;

    this.container.innerHTML = `
      <div
        class="banner-carousel"
        role="region"
        aria-roledescription="carousel"
        aria-label="Promociones destacadas"
        tabindex="0"
      >
        <div class="banner-live sr-only" aria-live="polite" aria-atomic="true" data-banner-live></div>
        <div class="banner-track">
          ${this.banners
            .map((banner, index) => {
              const slideId = `banner-slide-${index + 1}`;
              const titleId = `banner-title-${index + 1}`;
              const descriptionId = `banner-description-${index + 1}`;
              const icon = banner.icon ? escapeHtml(banner.icon) : "";
              const title = escapeHtml(banner.title || "Aviso");
              const description = escapeHtml(banner.description || "");
              const animatedClass = banner.iconAnimated ? "banner-icon--animated" : "";
              const linkData = getSafeLink(banner.link);
              const slideBody = `
                <div class="banner-content">
                  <p class="banner-eyebrow">Novedades del local</p>
                  <div class="banner-header">
                    ${
                      icon
                        ? `<div class="banner-icon ${animatedClass}" aria-hidden="true">${icon}</div>`
                        : ""
                    }
                    <h2 id="${titleId}" class="banner-title">${title}</h2>
                  </div>
                  ${
                    description
                      ? `<div class="banner-copy">
                    <p id="${descriptionId}" class="banner-description">${description}</p>
                  </div>`
                      : ""
                  }
                  ${
                    linkData
                      ? `
                    <div class="banner-cta-group">
                    
                      ${
                        linkData.isExternal
                          ? '<span class="banner-link-badge">Ir</span>'
                          : ""
                      }
                    </div>
                  `
                      : ""
                  }
                </div>
              `;

              const slideSurface = linkData
                ? `
                  <a
                    href="${escapeHtml(linkData.href)}"
                    class="banner-link"
                    ${linkData.isExternal ? 'target="_blank" rel="noopener noreferrer"' : ""}
                  >
                    ${slideBody}
                  </a>
                `
                : `<div class="banner-surface">${slideBody}</div>`;

              return `
                <article
                  id="${slideId}"
                  class="banner-slide ${index === 0 ? "active" : ""}"
                  data-index="${index}"
                  role="group"
                  aria-roledescription="slide"
                  aria-label="${index + 1} de ${totalSlides}"
                  aria-hidden="${index === 0 ? "false" : "true"}"
                  ${index === 0 ? "" : "hidden"}
                >
                  ${slideSurface}
                </article>
              `;
            })
            .join("")}
        </div>
        ${
          totalSlides > 1
            ? `
          <div class="banner-toolbar">
            <div class="banner-indicators" aria-label="Seleccionar banner">
              ${this.banners
                .map(
                  (banner, index) => `
                    <button
                      type="button"
                      class="banner-indicator ${index === 0 ? "active" : ""}"
                      data-index="${index}"
                      aria-label="Mostrar banner ${index + 1}: ${escapeHtml(banner.title || "Aviso")}"
                      ${index === 0 ? 'aria-current="true"' : ""}
                    ></button>
                  `
                )
                .join("")}
            </div>
            <div class="banner-actions">
              <button type="button" class="banner-controls" data-banner-action="prev">
                <span aria-hidden="true">&#8249;</span>
                <span class="sr-only">Banner anterior</span>
              </button>
              <button type="button" class="banner-controls" data-banner-action="next">
                <span aria-hidden="true">&#8250;</span>
                <span class="sr-only">Banner siguiente</span>
              </button>
              <button
                type="button"
                class="banner-autoplay"
                data-banner-action="toggle-autoplay"
                aria-pressed="${String(!this.userPaused)}"
              >
                ${this.userPaused ? "Reanudar rotacion" : "Pausar rotacion"}
              </button>
            </div>
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  cacheElements() {
    this.root = this.container.querySelector(".banner-carousel");
    this.liveRegion = this.container.querySelector("[data-banner-live]");
    this.prevButton = this.container.querySelector('[data-banner-action="prev"]');
    this.nextButton = this.container.querySelector('[data-banner-action="next"]');
    this.toggleButton = this.container.querySelector('[data-banner-action="toggle-autoplay"]');
    this.slides = Array.from(this.container.querySelectorAll(".banner-slide"));
    this.indicators = Array.from(this.container.querySelectorAll(".banner-indicator"));
  }

  bindEvents() {
    if (!this.root) return;

    this.root.addEventListener("click", (event) => {
      const control = event.target.closest("[data-banner-action], .banner-indicator");
      if (!control) return;

      if (control.matches(".banner-indicator")) {
        const nextIndex = Number(control.dataset.index);
        this.goTo(nextIndex, { announce: true });
        return;
      }

      const action = control.dataset.bannerAction;
      if (action === "prev") {
        this.prev({ announce: true });
      }

      if (action === "next") {
        this.next({ announce: true });
      }

      if (action === "toggle-autoplay") {
        this.toggleAutoPlay();
      }
    });

    this.root.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.prev({ announce: true });
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.next({ announce: true });
      }

      if (event.key === "Home") {
        event.preventDefault();
        this.goTo(0, { announce: true });
      }

      if (event.key === "End") {
        event.preventDefault();
        this.goTo(this.banners.length - 1, { announce: true });
      }
    });

    this.root.addEventListener("mouseenter", () => {
      this.pauseInteraction();
    });

    this.root.addEventListener("mouseleave", () => {
      this.resumeInteraction();
    });

    this.root.addEventListener("focusin", () => {
      this.pauseInteraction();
    });

    this.root.addEventListener("focusout", (event) => {
      if (!this.root.contains(event.relatedTarget)) {
        this.resumeInteraction();
      }
    });
  }

  announceCurrentBanner(prefix = "") {
    if (!this.liveRegion) return;

    const banner = this.banners[this.currentIndex];
    const description = banner?.description ? ` ${banner.description}` : "";
    const message = `${prefix}${banner?.title || "Banner actual"}.${description}`.trim();
    this.liveRegion.textContent = message;
  }

  goTo(index, { announce = false } = {}) {
    if (!this.slides.length) return;

    let nextIndex = index;
    if (nextIndex < 0) nextIndex = this.banners.length - 1;
    if (nextIndex >= this.banners.length) nextIndex = 0;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;
      slide.classList.toggle("active", isActive);
      slide.hidden = !isActive;
      slide.setAttribute("aria-hidden", String(!isActive));

      const link = slide.querySelector(".banner-link");
      if (link) {
        link.tabIndex = isActive ? 0 : -1;
      }
    });

    this.indicators.forEach((indicator, indicatorIndex) => {
      const isActive = indicatorIndex === nextIndex;
      indicator.classList.toggle("active", isActive);
      if (isActive) {
        indicator.setAttribute("aria-current", "true");
      } else {
        indicator.removeAttribute("aria-current");
      }
    });

    this.currentIndex = nextIndex;

    if (announce) {
      this.announceCurrentBanner(`Banner ${this.currentIndex + 1} de ${this.banners.length}. `);
    }
  }

  next(options) {
    this.goTo(this.currentIndex + 1, options);
  }

  prev(options) {
    this.goTo(this.currentIndex - 1, options);
  }

  startAutoPlay() {
    this.stopAutoPlay();

    if (this.banners.length <= 1 || this.userPaused) return;

    this.intervalId = setInterval(() => {
      if (!this.isInteractionPaused) {
        this.next();
      }
    }, this.autoPlayDelay);

    this.updateAutoPlayButton();
  }

  stopAutoPlay() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  pauseInteraction() {
    this.isInteractionPaused = true;
  }

  resumeInteraction() {
    this.isInteractionPaused = false;
  }

  toggleAutoPlay() {
    this.userPaused = !this.userPaused;

    if (this.userPaused) {
      this.stopAutoPlay();
      this.announceCurrentBanner("Rotacion pausada. ");
    } else {
      this.startAutoPlay();
      this.announceCurrentBanner("Rotacion reanudada. ");
    }

    this.updateAutoPlayButton();
  }

  updateAutoPlayButton() {
    if (!this.toggleButton) return;

    const isPlaying = !this.userPaused;
    this.toggleButton.setAttribute("aria-pressed", String(isPlaying));
    this.toggleButton.textContent = isPlaying ? "Pausar rotacion" : "Reanudar rotacion";
  }

  updateBanners(banners) {
    this.banners = banners.filter((banner) => banner.active);
    this.currentIndex = 0;
    this.init();
  }

  destroy() {
    this.stopAutoPlay();
    this.container.innerHTML = "";
  }
}

export default BannerCarousel;
